import { LfoModulatorBox, MacroModulatorBox, ModulationBox, RandomModulatorBox, StepsModulatorBox } from "@opendaw/studio-boxes";
import { IndexedBox, PrimitiveField } from "@opendaw/lib-box";
import { asInstanceOf, clamp, isNull, panic, Strings, UUID } from "@opendaw/lib-std";
import { Facade, Parameters, Props } from "./Common";
import { Guard } from "./Guard";
import { Facades } from "./Facades";
import { AudioUnitImpls } from "./AudioUnits";
import { TrackImpls, ValueTrackImpl } from "./timeline/Tracks";
import { TrackType } from "@opendaw/studio-adapters";
export class ModulationImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new ModulationImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({ depth: box.depth, enabled: box.enabled });
    }
    get source() {
        const field = this.box.source.targetVertex.unwrap("modulation has no source");
        return ModulatorImpls.wrap(this.context, field.box);
    }
    get targetField() { return this.box.target.targetVertex.unwrap("modulation has no target"); }
    get target() {
        const facade = Facades.forBox(this.context, this.targetField.box);
        if (isNull(facade)) {
            return panic(`No object found for modulation target ${this.targetField.toString()}`);
        }
        return facade;
    }
    get parameter() {
        const field = this.targetField;
        if (!(field instanceof PrimitiveField)) {
            return panic("modulation target is not a parameter");
        }
        return Parameters.pathOf(this.target, field) ?? panic(`Unknown parameter path for ${field.toString()}`);
    }
}
export class ModulatorFacade extends Facade {
    constructor(context, box) {
        super(context, box);
        this.bind({ label: box.label, enabled: box.enabled, bipolar: box.bipolar, amount: box.amount });
    }
    get index() { return this.box.index.getValue(); }
    get modulations() {
        return IndexedBox.collectIndexedBoxes(this.box.assignments)
            .map(box => ModulationImpl.wrap(this.context, asInstanceOf(box, ModulationBox)));
    }
    get valueTracks() {
        return TrackImpls.list(this.context, this.box.tracks)
            .filter((track) => track instanceof ValueTrackImpl);
    }
    addValueTrack(target, parameter, props) {
        const field = AudioUnitImpls.automationField(target, parameter);
        const ownerBox = field.box instanceof ModulationBox
            ? field.box.source.targetVertex.unwrap("modulation has no source").box
            : field.box;
        if (ownerBox !== this.box) {
            return panic(new RangeError(`'${parameter}' does not belong to this modulator`));
        }
        if (this.valueTracks.some(track => track.box.target.targetVertex.contains(field))) {
            return panic(new RangeError(`'${parameter}' is already automated by a track`));
        }
        return Props.apply(TrackImpls.create(this.context, this.box.tracks, TrackType.Value, field, null), props);
    }
    assign(target, parameter, depth = 0.25) {
        const field = AudioUnitImpls.modulationField(target, parameter);
        const validatedDepth = Guard.float32("bipolar", depth, "depth");
        return this.context.edit(() => {
            const box = ModulationBox.create(this.context.boxGraph, UUID.generate(), box => {
                box.source.refer(this.box.assignments);
                box.target.refer(field);
                box.index.setValue(this.box.assignments.pointerHub.incoming().length);
                box.depth.setValue(validatedDepth);
            });
            return ModulationImpl.wrap(this.context, box);
        });
    }
    remove() {
        this.context.edit(() => {
            const rootBox = this.context.skeleton.mandatoryBoxes.rootBox;
            const index = this.index;
            IndexedBox.removeOrder(rootBox.modulators, index);
            this.box.delete();
        });
    }
}
export class LfoModulatorImpl extends ModulatorFacade {
    kind = "LFO";
    constructor(context, box) {
        super(context, box);
        this.bind({ shape: box.shape, rateSync: box.rateSync, rateAbsolute: box.rateAbsolute, phase: box.phase, exponent: box.exponent });
    }
}
export class StepsModulatorImpl extends ModulatorFacade {
    kind = "Steps";
    constructor(context, box) {
        super(context, box);
        this.bind({
            count: box.count, rateSync: box.rateSync, rateAbsolute: box.rateAbsolute, phase: box.phase,
            smooth: box.smooth, direction: box.direction, steps: box.steps.fields()
        });
    }
    setSteps(values) {
        if (!Array.isArray(values)) {
            return panic(new TypeError("setSteps: expected an array"));
        }
        const fields = this.box.steps.fields();
        if (values.length > fields.length) {
            return panic(new RangeError(`setSteps: at most ${fields.length} steps`));
        }
        this.context.edit(() => {
            values.forEach((value, index) => fields[index].setValue(Guard.float32("unipolar", value, `steps.${index}`)));
            this.box.count.setValue(clamp(values.length, 1, fields.length));
        });
    }
}
export class MacroModulatorImpl extends ModulatorFacade {
    kind = "Macro";
    constructor(context, box) {
        super(context, box);
        this.bind({ value: box.value });
    }
}
export class RandomModulatorImpl extends ModulatorFacade {
    kind = "Random";
    constructor(context, box) {
        super(context, box);
        this.bind({
            loop: box.loop, rateSync: box.rateSync, rateAbsolute: box.rateAbsolute, phase: box.phase,
            smooth: box.smooth, seed: box.seed, levels: box.levels
        });
    }
}
export var ModulatorImpls;
(function (ModulatorImpls) {
    ModulatorImpls.Kinds = ["LFO", "Steps", "Macro", "Random"];
    ModulatorImpls.wrap = (context, box) => context.facade(box, () => {
        if (box instanceof LfoModulatorBox) {
            return new LfoModulatorImpl(context, box);
        }
        if (box instanceof StepsModulatorBox) {
            return new StepsModulatorImpl(context, box);
        }
        if (box instanceof MacroModulatorBox) {
            return new MacroModulatorImpl(context, box);
        }
        if (box instanceof RandomModulatorBox) {
            return new RandomModulatorImpl(context, box);
        }
        return panic(`${box.name} is not a modulator`);
    });
    ModulatorImpls.list = (context) => IndexedBox.collectIndexedBoxes(context.skeleton.mandatoryBoxes.rootBox.modulators)
        .map(box => ModulatorImpls.wrap(context, box));
    ModulatorImpls.create = (context, kind, props) => {
        Guard.oneOf(kind, ModulatorImpls.Kinds, "kind");
        return context.edit(() => {
            const { boxGraph, mandatoryBoxes: { rootBox } } = context.skeleton;
            const existing = ModulatorImpls.list(context);
            const label = Strings.getUniqueName(existing.map(modulator => modulator.label), kind);
            const attach = (box) => {
                box.collection.refer(rootBox.modulators);
                box.index.setValue(existing.length);
                box.label.setValue(label);
            };
            const box = kind === "LFO"
                ? LfoModulatorBox.create(boxGraph, UUID.generate(), attach)
                : kind === "Steps"
                    ? StepsModulatorBox.create(boxGraph, UUID.generate(), attach)
                    : kind === "Macro"
                        ? MacroModulatorBox.create(boxGraph, UUID.generate(), attach)
                        : RandomModulatorBox.create(boxGraph, UUID.generate(), attach);
            return Props.apply(ModulatorImpls.wrap(context, box), props);
        });
    };
})(ModulatorImpls || (ModulatorImpls = {}));
